import type { GetStaticProps } from "next";
import React from "react";
import {
    SectionPage,
    ArticleWrapper,
    CategoryRowsPage,
    mergeTaxonomyTree,
    mergedTreeToBuildKitTaxonomies,
} from "@orlandohealth/build-kit";
import { slugify } from "@orlandohealth/utils";
import { builder } from "@builder.io/sdk";

interface BuildKitCategory {
    name: string;
    slug: string;
    taxonomies?: any[];
}

interface B2BCategoriesIndexProps {
    siteProperties: any;
    taxonomies: BuildKitCategory[];
    articlesByCategory: Record<string, any[]>;
}

export const getStaticProps: GetStaticProps = async ({ locale, defaultLocale }) => {
    const resolvedLocale = locale || defaultLocale || "en_US";
    const flApiKey = process.env.NEXT_PUBLIC_BUILDER_API_KEY || "";
    const corpApiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";

    const baseProps = await SectionPage.getStaticProps(
        "/medical-professionals/the-update",
        "categories",
        locale,
        defaultLocale,
        "page"
    );

    const page = await builder
        .get("page", {
            userAttributes: { urlPath: "/medical-professionals/the-update" },
            locale: resolvedLocale,
            apiKey: flApiKey,
        })
        .promise();

    // Fetch taxonomy WITHOUT locale so name fields stay as Builder LocalizedValue
    // objects; mergeTaxonomyTree resolves localized display names while preserving
    // Default-locale `defaultName` values for slug generation.
    let taxonomyData = null;
    if (page?.data?.taxonomy?.referenceId) {
        taxonomyData = await builder.get("taxonomy", {
            options: { includeRefs: true },
            query: { id: page?.data?.taxonomy?.referenceId },
            apiKey: corpApiKey,
        }).promise();
    } else {
        taxonomyData = await builder.get("taxonomy", {
            options: { includeRefs: true },
            query: { name: "B2B Categories" },
            apiKey: flApiKey,
        }).promise();

        if (!taxonomyData?.data) {
            taxonomyData = await builder.get("taxonomy", {
                options: { includeRefs: true },
                query: { name: "content-hub" },
                apiKey: flApiKey,
            }).promise();
        }
    }

    const merged = mergeTaxonomyTree(taxonomyData?.data?.taxonomies, resolvedLocale);
    const taxonomies = mergedTreeToBuildKitTaxonomies(merged, slugify);

    // Fetch top 3 articles for each top-level category in parallel. Query Builder by
    // the Default-locale category name so the article match is locale-independent.
    const articlesByCategory: Record<string, any[]> = {};

    await Promise.all(
        merged.map(async (category, idx) => {
            const slug = taxonomies[idx]?.slug;
            if (!slug) return;
            try {
                const results = await builder.getAll("article", {
                    locale: resolvedLocale,
                    limit: 3,
                    options: { includeRefs: true },
                    query: {
                        "data.taxonomies.tags": {
                            $regex: category.defaultName,
                            $options: "i",
                        },
                        "data.metadata.targetingScope": { $eq: "b2b" },
                    },
                    apiKey: corpApiKey,
                });
                articlesByCategory[slug] = results || [];
            } catch (e) {
                console.error(
                    `Error fetching articles for category ${slug}: ${e instanceof Error ? e.message : String(e)}`
                );
                articlesByCategory[slug] = [];
            }
        })
    );

    return {
        props: {
            ...baseProps.props,
            taxonomies,
            articlesByCategory,
        },
        revalidate: 60,
    };
};

const B2BCategoriesIndex: React.FC<B2BCategoriesIndexProps> = (props) => {
    const categoryRows = props.taxonomies
        .map((cat) => ({
            name: cat.name,
            slug: cat.slug,
            articles: props.articlesByCategory[cat.slug] || [],
        }))
        .filter((row) => row.slug);
    return (
        <ArticleWrapper {...props} url="/medical-professionals/the-update" taxonomies={props.taxonomies}>
            <CategoryRowsPage
                categoryRows={categoryRows}
                baseUrl="/medical-professionals/the-update"
                heading="The Update"
            />
        </ArticleWrapper>
    );
};

export default B2BCategoriesIndex;
